// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license, please see LICENSE.md in the
// project root for license information or contact permission@sei.cmu.edu for full terms.

import { A11yModule } from '@angular/cdk/a11y';
import { CdkTableModule } from '@angular/cdk/table';
import { CdkTreeModule } from '@angular/cdk/tree';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  ComnAuthModule,
  CRUCIBLE_DIALOG_IMPORTS,
  ComnHeaderBarModule,
  ComnSettingsConfig,
  ComnSettingsModule,
  ComnSettingsService,
  provideCrucibleTheme,
} from '@cmusei/crucible-common';
import { AkitaNgRouterStoreModule } from '@datorama/akita-ng-router-store';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AdminActionsComponent } from './components/admin/admin-actions/admin-actions.component';
import { AdminActionEditDialogComponent } from './components/admin/admin-action-edit-dialog/admin-action-edit-dialog.component';
import { AdminContainerComponent } from './components/admin/admin-container/admin-container.component';
import { AdminEvaluationEditDialogComponent } from './components/admin/admin-evaluation-edit-dialog/admin-evaluation-edit-dialog.component';
import { AdminEvaluationsComponent } from './components/admin/admin-evaluations/admin-evaluations.component';
import { AdminMovesComponent } from './components/admin/admin-moves/admin-moves.component';
import { AdminMoveEditDialogComponent } from './components/admin/admin-move-edit-dialog/admin-move-edit-dialog.component';
import { AdminPreviewComponent } from './components/admin/admin-preview/admin-preview.component';
import { AdminDutyEditDialogComponent } from './components/admin/admin-duty-edit-dialog/admin-duty-edit-dialog.component';
import { AdminDutiesComponent } from 'src/app/components/admin/admin-duties/admin-duties.component';
import { AdminScoringCategoriesComponent } from './components/admin/admin-scoring-categories/admin-scoring-categories.component';
import { AdminScoringCategoryEditDialogComponent } from './components/admin/admin-scoring-category-edit-dialog/admin-scoring-category-edit-dialog.component';
import { AdminScoringModelsComponent } from './components/admin/admin-scoring-models/admin-scoring-models.component';
import { AdminScoringModelEditDialogComponent } from './components/admin/admin-scoring-model-edit-dialog/admin-scoring-model-edit-dialog.component';
import { AdminScoringOptionsComponent } from './components/admin/admin-scoring-options/admin-scoring-options.component';
import { AdminScoringOptionEditDialogComponent } from './components/admin/admin-scoring-option-edit-dialog/admin-scoring-option-edit-dialog.component';
import { AdminSubmissionsComponent } from './components/admin/admin-submissions/admin-submissions.component';
import { AdminTeamsComponent } from './components/admin/admin-teams/admin-teams.component';
import { AdminTeamEditDialogComponent } from './components/admin/admin-team-edit-dialog/admin-team-edit-dialog.component';
import { AdminTeamTypesComponent } from './components/admin/admin-teamtypes/admin-teamtypes.component';
import { AdminTeamTypeEditDialogComponent } from './components/admin/admin-teamtype-edit-dialog/admin-teamtype-edit-dialog.component';
import { AdminTeamMembershipsComponent } from './components/admin/admin-team-memberships/admin-team-memberships/admin-team-memberships.component';
import { AdminTeamMembershipListComponent } from './components/admin/admin-team-memberships/admin-team-membership-list/admin-team-membership-list.component';
import { AdminTeamMemberListComponent } from './components/admin/admin-team-memberships/admin-team-member-list/admin-team-member-list.component';
import { AdminUsersComponent } from './components/admin/admin-users/admin-users.component';
import { AggregateComponent } from './components/aggregate/aggregate.component';
import { ClipboardModule } from 'ngx-clipboard';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { HomeAppComponent } from './components/home-app/home-app.component';
import { EvaluationInfoComponent } from './components/evaluation-info/evaluation-info.component';
import { ScoreSummaryComponent } from './components/score-summary/score-summary.component';
import { ScoresheetComponent } from './components/scoresheet/scoresheet.component';
import { ReportComponent } from './components/report/report.component';
import { AdminGroupsComponent } from './components/admin/admin-groups/admin-groups.component';
import { AdminGroupsDetailComponent } from './components/admin/admin-groups/admin-groups-detail/admin-groups-detail.component';
import { AdminGroupsMemberListComponent } from './components/admin/admin-groups/admin-groups-member-list/admin-groups-member-list.component';
import { AdminGroupsMembershipListComponent } from './components/admin/admin-groups/admin-groups-membership-list/admin-groups-membership-list.component';
import { AdminRolesComponent } from './components/admin/admin-roles/admin-roles.component';
import { AdminEvaluationRolesComponent } from './components/admin/admin-roles/admin-evaluation-roles/admin-evaluation-roles.component';
import { AdminScoringModelRolesComponent } from './components/admin/admin-roles/admin-scoring-model-roles/admin-scoring-model-roles.component';
import { AdminScoringModelMembershipsComponent } from './components/admin/admin-scoring-model-memberships/admin-scoring-model-memberships/admin-scoring-model-memberships.component';
import { AdminScoringModelMemberListComponent } from './components/admin/admin-scoring-model-memberships/admin-scoring-model-member-list/admin-scoring-model-member-list.component';
import { AdminScoringModelMembershipListComponent } from './components/admin/admin-scoring-model-memberships/admin-scoring-model-membership-list/admin-scoring-model-membership-list.component';
import { AdminEvaluationMembershipsComponent } from './components/admin/admin-evaluation-memberships/admin-evaluation-memberships/admin-evaluation-memberships.component';
import { AdminEvaluationMemberListComponent } from './components/admin/admin-evaluation-memberships/admin-evaluation-member-list/admin-evaluation-member-list.component';
import { AdminEvaluationMembershipListComponent } from './components/admin/admin-evaluation-memberships/admin-evaluation-membership-list/admin-evaluation-membership-list.component';
import { AdminSystemRolesComponent } from './components/admin/admin-roles/admin-system-roles/admin-system-roles.component';
import { AdminUserListComponent } from './components/admin/admin-users/admin-user-list/admin-user-list.component';
import { NameDialogComponent } from './components/shared/name-dialog/name-dialog.component';
import { RightSideHtmlComponent } from './components/right-side-html/right-side-html.component';
import { RightSideIframeComponent } from './components/right-side-iframe/right-side-iframe.component';
import { SystemMessageComponent } from './components/shared/system-message/system-message.component';
import { TopbarComponent } from './components/shared/top-bar/topbar.component';
import { UserDataService } from './data/user/user-data.service';
import { ErrorService } from './services/error/error.service';
import { SystemMessageService } from './services/system-message/system-message.service';
import { BASE_PATH } from './generated/cite.api';
import { ApiModule as SwaggerCodegenApiModule } from './generated/cite.api/api.module';
import { DisplayOrderPipe, SortByPipe } from 'src/app/utilities/sort-by-pipe';
import { UIDataService } from './data/ui/ui-data.service';
import { AngularEditorModule } from '@kolkov/angular-editor';
import {
  NgxMatDatepickerActions,
  NgxMatDatepickerApply,
  NgxMatDatepickerCancel,
  NgxMatDatepickerClear,
  NgxMatDatepickerInput,
  NgxMatDatepickerToggle,
  NgxMatDatetimepicker,
} from '@ngxmc/datetime-picker';


const settings: ComnSettingsConfig = {
  url: 'assets/config/settings.json',
  sharedUrl: 'assets/config/settings.shared.json',
  envUrl: 'assets/config/settings.env.json',
};

export function getBasePath(settingsSvc: ComnSettingsService)
 {
  return settingsSvc.settings.ApiUrl;
}

@NgModule({
  declarations: [
    AppComponent,
    HomeAppComponent,
    EvaluationInfoComponent,
    ScoreSummaryComponent,
    ScoresheetComponent,
    AggregateComponent,
    ReportComponent,
    SystemMessageComponent,
    AdminActionsComponent,
    AdminActionEditDialogComponent,
    AdminContainerComponent,
    AdminEvaluationEditDialogComponent,
    AdminEvaluationsComponent,
    AdminMovesComponent,
    AdminMoveEditDialogComponent,
    AdminPreviewComponent,
    AdminDutyEditDialogComponent,
    AdminDutiesComponent,
    AdminScoringCategoriesComponent,
    AdminScoringCategoryEditDialogComponent,
    AdminScoringModelsComponent,
    AdminScoringModelEditDialogComponent,
    AdminScoringOptionsComponent,
    AdminScoringOptionEditDialogComponent,
    AdminSubmissionsComponent,
    AdminTeamsComponent,
    AdminTeamEditDialogComponent,
    AdminTeamTypesComponent,
    AdminTeamTypeEditDialogComponent,
    AdminTeamMembershipsComponent,
    AdminTeamMembershipListComponent,
    AdminTeamMemberListComponent,
    AdminUsersComponent,
    AdminUserListComponent,
    AdminGroupsComponent,
    AdminGroupsDetailComponent,
    AdminGroupsMemberListComponent,
    AdminGroupsMembershipListComponent,
    AdminRolesComponent,
    AdminEvaluationRolesComponent,
    AdminScoringModelRolesComponent,
    AdminSystemRolesComponent,
    AdminScoringModelMemberListComponent,
    AdminScoringModelMembershipListComponent,
    AdminScoringModelMembershipsComponent,
    AdminEvaluationMemberListComponent,
    AdminEvaluationMembershipListComponent,
    AdminEvaluationMembershipsComponent,
    DashboardComponent,
    NameDialogComponent,
    RightSideHtmlComponent,
    RightSideIframeComponent,
    TopbarComponent,
    DisplayOrderPipe,
    SortByPipe,
  ],
  exports: [MatSortModule],
  bootstrap: [AppComponent],
  imports: [
    environment.production ? [] : AkitaNgDevtools.forRoot(),
    AkitaNgRouterStoreModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    A11yModule,
    ClipboardModule,
    SwaggerCodegenApiModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatStepperModule,
    MatBottomSheetModule,
    MatBadgeModule,
    MatFormFieldModule,
    NgxMatDatepickerActions,
    NgxMatDatepickerApply,
    NgxMatDatepickerCancel,
    NgxMatDatepickerClear,
    NgxMatDatepickerInput,
    NgxMatDatepickerToggle,
    NgxMatDatetimepicker,
    CdkTableModule,
    MatTreeModule,
    CdkTreeModule,
    ComnAuthModule.forRoot(),
    ComnSettingsModule.forRoot(),
    AngularEditorModule,
    ComnHeaderBarModule,
    ...CRUCIBLE_DIALOG_IMPORTS,
  ],
  providers: [
    SystemMessageService,
    UserDataService,
    UIDataService,
    {
      provide: BASE_PATH,
      useFactory: getBasePath,
      deps: [ComnSettingsService],
    },
    {
      provide: ErrorHandler,
      useClass: ErrorService,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideCrucibleTheme({
      brand: { color: '#E81717', text: '#FFFFFF' },
      faviconFillClass: 'cls-2',
    }),
  ],
})
export class AppModule {}
